import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Play, AlertTriangle, CheckCircle, Database, Loader2, QrCode } from "lucide-react";

import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDataSync() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // "m4" | "m5" | "all" | null
  const [lastResult, setLastResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingJob, setPendingJob] = useState(null); // {job, dry_run}
  const [includePending, setIncludePending] = useState(false);

  // QR backfill state — rewrites old "UEN text" invoices to embedded QR
  const [qrRunning, setQrRunning] = useState(false);
  const [qrResult, setQrResult] = useState(null);

  const runQrBackfill = async (dryRun) => {
    setQrRunning(true);
    setQrResult(null);
    try {
      const res = await axios.post(
        `${API}/payments/admin/backfill-invoice-qr${dryRun ? "?dry_run=true" : ""}`,
        {},
        { withCredentials: true }
      );
      setQrResult({ ...res.data, dry_run: dryRun });
      if (res.data.total_candidates === 0) {
        toast.success("No old-format invoices found — everything already has a QR!");
      } else if (dryRun) {
        toast.info(`Found ${res.data.total_candidates} old invoice(s) that would be updated`);
      } else {
        toast.success(`Updated ${res.data.updated} invoice(s)${res.data.errored > 0 ? ` · ${res.data.errored} errored` : ""}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setQrRunning(false);
    }
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/data-sync/status`, {
        withCredentials: true,
      });
      setStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load sync status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const askConfirm = (job, dry_run) => {
    setPendingJob({ job, dry_run });
    setConfirmOpen(true);
  };

  const runSync = async () => {
    if (!pendingJob) return;
    setConfirmOpen(false);
    setRunning(pendingJob.job);
    try {
      const res = await axios.post(
        `${API}/academy/admin/data-sync/run`,
        { ...pendingJob, include_pending: includePending },
        { withCredentials: true }
      );
      setLastResult(res.data);
      toast.success(
        pendingJob.dry_run
          ? `Dry-run complete for ${pendingJob.job.toUpperCase()}`
          : `Sync complete — ${pendingJob.job.toUpperCase()}`
      );
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setRunning(null);
      setPendingJob(null);
    }
  };

  const statusChip = (value, goodWhenZero = true) => {
    const isGood = goodWhenZero ? value === 0 : value > 0;
    return (
      <span
        className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold border ${
          isGood
            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
            : "bg-amber-500/15 text-amber-300 border-amber-500/40"
        }`}
      >
        {value}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 p-6 md:p-10">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
          data-testid="back-to-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin Panel
        </Link>

        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <Database className="w-10 h-10 text-sky-400" />
              Data Sync
            </h1>
            <p className="text-slate-400 mt-2 max-w-2xl">
              One-click tools to heal your payment records. Use these whenever the
              Payment Tracking page shows "drift" badges or approved parents are
              missing from team rosters.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchStatus}
            disabled={loading}
            className="border-slate-700"
            data-testid="refresh-sync-status"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Status Overview */}
        <Card className="p-6 bg-slate-900/70 border border-slate-700 mb-6" data-testid="sync-status-card">
          <h2 className="text-xl font-semibold text-white mb-4">Current Health</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking…
            </div>
          ) : status ? (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-slate-400">Paid transactions</p>
                <p className="text-3xl font-bold text-white mt-1">{status.paid_transactions}</p>
                <p className="text-xs text-slate-500 mt-2">Total paid records in system</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-slate-400 flex items-center">
                  Status drift {statusChip(status.status_drift_count)}
                </p>
                <p className="text-3xl font-bold text-white mt-1">{status.status_drift_count}</p>
                <p className="text-xs text-slate-500 mt-2">
                  Paid transactions whose registration still says "pending". Run <b>M4</b> to fix.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-slate-400 flex items-center">
                  Missing enrollments {statusChip(status.missing_enrollments_count)}
                </p>
                <p className="text-3xl font-bold text-white mt-1">{status.missing_enrollments_count}</p>
                <p className="text-xs text-slate-500 mt-2">
                  Children who should be on rosters but have no enrollment row yet. Run <b>M5</b> to fix.
                </p>
              </div>
            </div>
          ) : null}

          {status && !status.needs_sync && !loading && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-2" data-testid="sync-all-clear">
              <CheckCircle className="w-4 h-4" />
              All clean — your payment records and enrollments are in sync.
            </div>
          )}
        </Card>

        {/* M4 Card */}
        <Card className="p-6 bg-slate-900/70 border border-emerald-500/20 mb-6" data-testid="m4-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">M4 — Payment Status Sync</h3>
              <p className="text-sm text-slate-400 mt-1">
                For every approved transaction, marks its registration record as
                <code className="mx-1 px-1.5 py-0.5 bg-slate-800 rounded text-emerald-300">payment_status = paid</code>.
                Fixes the "all showing pending" issue. Idempotent — safe to re-run anytime.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => askConfirm("m4", true)}
                disabled={!!running}
                className="border-slate-700"
                data-testid="m4-dry-run"
              >
                Dry-run
              </Button>
              <Button
                size="sm"
                onClick={() => askConfirm("m4", false)}
                disabled={!!running}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="m4-run"
              >
                {running === "m4" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Run M4
              </Button>
            </div>
          </div>
        </Card>

        {/* M5 Card */}
        <Card className="p-6 bg-slate-900/70 border border-sky-500/20 mb-6" data-testid="m5-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">M5 — Auto-generate Enrollments</h3>
              <p className="text-sm text-slate-400 mt-1">
                For every approved registration, creates the <code className="mx-1 px-1.5 py-0.5 bg-slate-800 rounded text-sky-300">academy_enrollments</code> row
                and links the child to their training sessions so they appear on rosters/attendance. Idempotent.
              </p>
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-300">
                <Switch checked={includePending} onCheckedChange={setIncludePending} id="include-pending" data-testid="include-pending-switch" />
                <label htmlFor="include-pending" className="cursor-pointer">
                  Include pending registrations (so unpaid kids still show up on attendance)
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => askConfirm("m5", true)}
                disabled={!!running}
                className="border-slate-700"
                data-testid="m5-dry-run"
              >
                Dry-run
              </Button>
              <Button
                size="sm"
                onClick={() => askConfirm("m5", false)}
                disabled={!!running}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="m5-run"
              >
                {running === "m5" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Run M5
              </Button>
            </div>
          </div>
        </Card>

        {/* QR Backfill Card — fixes old invoices still showing UEN text block */}
        <Card className="p-6 bg-slate-900/70 border border-amber-500/30 mb-6" data-testid="qr-backfill-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <QrCode className="w-5 h-5 text-amber-400" />
                Fix Old Invoices (QR Backfill)
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Rewrites old stored invoices that still show the plain-text
                <code className="mx-1 px-1.5 py-0.5 bg-slate-800 rounded text-amber-300">UEN: 202436060M</code>
                block → replaces it with a scannable PayNow QR + the "do not change reference" warning.
                Idempotent — safe to re-run anytime. New invoices already use the QR format.
              </p>
              {qrResult && (
                <div className="mt-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm">
                  <p className="text-slate-300">
                    {qrResult.dry_run && <span className="text-amber-400 mr-2">[dry-run]</span>}
                    <strong>{qrResult.total_candidates}</strong> old-format invoice(s) found ·
                    <strong className="text-emerald-300 ml-1">{qrResult.updated}</strong> updated ·
                    <strong className="text-slate-400 ml-1">{qrResult.skipped}</strong> skipped
                    {qrResult.errored > 0 && <strong className="text-red-400 ml-1">· {qrResult.errored} errored</strong>}
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => runQrBackfill(true)}
                disabled={qrRunning}
                className="border-slate-700"
                data-testid="qr-backfill-dry-run"
              >
                Dry-run
              </Button>
              <Button
                size="sm"
                onClick={() => runQrBackfill(false)}
                disabled={qrRunning}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="qr-backfill-run"
              >
                {qrRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Run Backfill
              </Button>
            </div>
          </div>
        </Card>

        {/* Run All */}
        <Card className="p-6 bg-slate-900/70 border border-purple-500/30 mb-6" data-testid="run-all-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Run Everything</h3>
              <p className="text-sm text-slate-400 mt-1">Runs M4 then M5 in one go — the most common healing sequence.</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => askConfirm("all", true)}
                disabled={!!running}
                className="border-slate-700"
                data-testid="all-dry-run"
              >
                Dry-run All
              </Button>
              <Button
                onClick={() => askConfirm("all", false)}
                disabled={!!running}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="all-run"
              >
                {running === "all" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Run All
              </Button>
            </div>
          </div>
        </Card>

        {/* Last Result */}
        {lastResult && (
          <Card className="p-6 bg-slate-900/70 border border-slate-700" data-testid="sync-result-card">
            <h3 className="text-lg font-semibold text-white mb-2">
              Last run — {lastResult.job.toUpperCase()} {lastResult.dry_run && <span className="text-amber-400 text-sm ml-2">(dry-run)</span>}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Ran at {new Date(lastResult.ran_at).toLocaleString()} by {lastResult.ran_by}
            </p>
            <pre className="text-xs bg-black/40 p-4 rounded-lg overflow-x-auto text-slate-300 border border-slate-800">
              {JSON.stringify(lastResult.results, null, 2)}
            </pre>
          </Card>
        )}
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm {pendingJob?.dry_run ? "dry-run" : "sync"} — {pendingJob?.job?.toUpperCase()}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {pendingJob?.dry_run
                ? "Dry-run will scan your data and show what WOULD be changed — no writes."
                : "This will update your live data. The operation is idempotent and safe to re-run, but double-check the dry-run first if unsure."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button
              onClick={runSync}
              className={pendingJob?.dry_run ? "bg-slate-600 hover:bg-slate-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="confirm-sync-run"
            >
              {pendingJob?.dry_run ? "Run Dry-run" : "Run Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
